import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/LanguageContext";
import { Reveal, ChapterLabel } from "@/components/Reveal";
import { IMAGES, SERVICE_IMAGES } from "@/content";
import { COUNTRIES, DESTINATIONS, COUNTRY_HERO, COLLECTIONS, COLLECTION_MEMBERS } from "@/data/destinations";

/* ---------------- Destinations by Country ---------------- */
export const Destinations = () => {
  const { lang } = useLang();
  const isFr = lang === "fr";
  const navigate = useNavigate();

  const goCountry = (slug) => {
    navigate(`/destinations/${slug}`);
    window.scrollTo(0, 0);
  };

  return (
    <section id="destinations" data-testid="destinations-band" className="bg-ivory py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal className="flex flex-col items-center text-center mb-12">
          <span className="tricolore" style={{ width: "160px", height: "3px" }}>
            <span className="bg-frblue" /><span className="bg-white" /><span className="bg-frred" />
          </span>
          <span className="text-gold text-xs tracking-luxe uppercase mt-6">Miami · France · Europe</span>
          <h2 className="font-serif-lux text-navy text-4xl sm:text-5xl lg:text-6xl font-light mt-5 max-w-3xl">
            {isFr ? "Nos Destinations d'Exception" : "Our Signature Destinations"}
          </h2>
          <p className="text-navy/60 font-light max-w-xl mt-5">
            {isFr
              ? "Choisissez un pays pour découvrir ses destinations et leurs expériences signature."
              : "Choose a country to discover its destinations and their signature experiences."}
          </p>
        </Reveal>

        <div data-testid="country-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {COUNTRIES.map((c, i) => {
            const cities = DESTINATIONS.filter((d) => d.country === c.slug);
            if (!cities.length) return null;
            return (
              <motion.button
                key={c.slug}
                data-testid={`country-${c.slug}`}
                onClick={() => goCountry(c.slug)}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.07 }}
                className="group relative overflow-hidden h-[300px] text-left cursor-pointer"
              >
                <img
                  src={COUNTRY_HERO[c.slug]}
                  alt={isFr ? c.fr : c.en}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/35 to-transparent" />
                <div className="absolute inset-0 border border-transparent group-hover:border-gold/60 m-3 transition-all duration-500 pointer-events-none" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-gold-light text-[10px] tracking-luxe uppercase">
                    {c.noteEn ? (isFr ? c.noteFr : c.noteEn) : `${cities.length} ${isFr ? (cities.length > 1 ? "destinations" : "destination") : (cities.length > 1 ? "destinations" : "destination")}`}
                  </p>
                  <h3 className="font-serif-lux text-white text-3xl md:text-4xl leading-tight mt-1 group-hover:text-gold-light transition-colors duration-500">
                    {isFr ? c.fr : c.en}
                  </h3>
                  <p className="text-white/60 text-xs font-light mt-2 truncate">
                    {cities.slice(0, 5).map((d) => (isFr ? d.nameFr : d.name)).join(" · ")}{cities.length > 5 ? " …" : ""}
                  </p>
                  <p className="text-gold text-[10px] tracking-luxe uppercase mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    {isFr ? "Découvrir →" : "Discover →"}
                  </p>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Lapland Christmas Feature ---------------- */
export const LaplandFeature = () => {
  const { lang } = useLang();
  const isFr = lang === "fr";
  const navigate = useNavigate();

  const experiences = isFr
    ? ["Village du Père Noël à Rovaniemi", "Aurores boréales en observation privée", "Chalets de luxe & igloos de verre", "Safaris en chiens de traîneau"]
    : ["Santa Claus Village in Rovaniemi", "Private Northern Lights viewing", "Luxury chalets & glass igloos", "Dog-sledding safaris"];

  const book = () => {
    const summary = isFr
      ? "Noël en Laponie — Village du Père Noël, aurores boréales, chalet de luxe"
      : "Christmas in Lapland — Santa Claus Village, Northern Lights, luxury chalet";
    sessionStorage.setItem("ftc_search", JSON.stringify({ type: "lapland", placeLabel: isFr ? "Laponie" : "Lapland", summary }));
    window.dispatchEvent(new CustomEvent("ftc:search", { detail: { type: "lapland", placeLabel: isFr ? "Laponie" : "Lapland", summary } }));
    navigate("/#contact");
    setTimeout(() => {
      const el = document.getElementById("contact");
      if (el) (window.__lenis ? window.__lenis.scrollTo(el, { offset: -20 }) : el.scrollIntoView());
    }, 150);
  };

  return (
    <section id="laponie" data-testid="lapland-section" className="relative overflow-hidden bg-navy-deep">
      <img
        src="https://images.unsplash.com/photo-1531366936337-7c912a4589a7?auto=format&fit=crop&w=1600&q=80"
        alt="" loading="lazy"
        className="absolute inset-0 w-full h-full object-cover opacity-40"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-navy-deep via-navy-deep/80 to-navy-deep/40" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <span className="tricolore" style={{ width: "140px", height: "3px" }}>
            <span className="bg-frblue" /><span className="bg-white" /><span className="bg-frred" />
          </span>
          <span className="text-gold text-xs tracking-luxe uppercase mt-6 block">
            {isFr ? "Édition Noël — Nouvelle Destination" : "Christmas Edition — New Destination"}
          </span>
          <h2 className="font-serif-lux text-white text-4xl sm:text-5xl lg:text-6xl font-light mt-5">
            {isFr ? "Noël en Laponie" : "Christmas in Lapland"}
          </h2>
          <p className="text-white/70 font-light max-w-xl mt-5">
            {isFr
              ? "Rovaniemi vous ouvre ses portes : le Village du Père Noël, des aurores boréales en privé et des chalets de luxe au cœur de la forêt enneigée — un Noël que vos enfants n'oublieront jamais."
              : "Rovaniemi opens its doors to you: Santa Claus Village, private Northern Lights and luxury chalets in the heart of the snowy forest — a Christmas your children will never forget."}
          </p>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mt-8">
            {experiences.map((e, i) => (
              <div key={i} data-testid={`lapland-exp-${i}`} className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rotate-45 bg-gold flex-shrink-0" />
                <span className="text-white/85 text-sm font-light">{e}</span>
              </div>
            ))}
          </div>
          <button
            data-testid="lapland-cta"
            onClick={book}
            className="btn-lux mt-10 bg-gold text-navy-deep hover:bg-gold-light px-8 py-3 text-xs tracking-luxe uppercase transition-colors"
          >
            {isFr ? "Réserver Noël en Laponie" : "Book Christmas in Lapland"}
          </button>
        </Reveal>
        <Reveal delay={0.15}>
          <div data-testid="lapland-img" className="relative">
            <div className="absolute -inset-3 border border-gold/40 pointer-events-none" />
            <img
              src="/santa-village-night.jpg"
              alt={isFr ? "Village du Père Noël, Rovaniemi" : "Santa Claus Village, Rovaniemi"}
              loading="lazy"
              className="w-full h-[320px] md:h-[420px] object-cover"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- Curated Collections ---------------- */
export const Collections = () => {
  const { lang } = useLang();
  const isFr = lang === "fr";
  const navigate = useNavigate();

  return (
    <section id="collections" data-testid="collections-section" className="bg-navy-deep py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal className="flex flex-col items-center text-center mb-12">
          <span className="tricolore" style={{ width: "160px", height: "3px" }}>
            <span className="bg-frblue" /><span className="bg-white" /><span className="bg-frred" />
          </span>
          <span className="text-gold text-xs tracking-luxe uppercase mt-6">
            {isFr ? "Six univers de voyage" : "Six worlds of travel"}
          </span>
          <h2 className="font-serif-lux text-white text-4xl sm:text-5xl lg:text-6xl font-light mt-5 max-w-3xl">
            {isFr ? "Nos Collections" : "Our Collections"}
          </h2>
          <p className="text-white/60 font-light max-w-xl mt-5">
            {isFr
              ? "Des destinations choisies par style de voyage — de la Méditerranée au Grand Nord."
              : "Destinations curated by travel style — from the Mediterranean to the far North."}
          </p>
        </Reveal>

        <div data-testid="collection-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {COLLECTIONS.map((col, i) => {
            const n = (COLLECTION_MEMBERS[col.slug] || []).length;
            return (
              <motion.button
                key={col.slug}
                data-testid={`collection-${col.slug}`}
                onClick={() => { navigate(`/collections/${col.slug}`); window.scrollTo(0, 0); }}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.07 }}
                className="group relative overflow-hidden h-[280px] text-left cursor-pointer"
              >
                <img
                  src={col.img}
                  alt={isFr ? col.fr : col.en}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy/40 to-transparent" />
                <div className="absolute inset-0 border border-transparent group-hover:border-gold/60 m-3 transition-all duration-500 pointer-events-none" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-gold-light text-[10px] tracking-luxe uppercase">
                    {n} {isFr ? "destinations" : "destinations"}
                  </p>
                  <h3 className="font-serif-lux text-white text-3xl md:text-4xl leading-tight mt-1 group-hover:text-gold-light transition-colors duration-500">
                    {isFr ? col.fr : col.en}
                  </h3>
                  <p className="text-white/60 text-xs font-light mt-2">{isFr ? col.descFr : col.descEn}</p>
                  <p className="text-gold text-[10px] tracking-luxe uppercase mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    {isFr ? "Explorer →" : "Explore →"}
                  </p>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Property & Yacht Search ---------------- */
const SEARCH_BUDGETS = ["€25k – €50k", "€50k – €100k", "€100k – €250k", "€250k+"];

export const PropertyYachtSearch = () => {
  const { lang } = useLang();
  const isFr = lang === "fr";
  const navigate = useNavigate();
  const [tab, setTab] = React.useState("property");
  const [f, setF] = React.useState({ place: "", sizeMin: "", sizeMax: "", bedrooms: "", budget: "", length: "", brand: "", cabins: "" });
  const upd = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const inputCls =
    "w-full bg-transparent border-0 border-b border-navy/25 rounded-none px-0 py-3 text-navy placeholder:text-navy/40 focus:outline-none focus:border-gold transition-colors text-sm";
  const labelCls = "block text-gold text-[11px] tracking-luxe uppercase mb-1";

  const submit = (e) => {
    e.preventDefault();
    let placeLabel = "", summary;
    if (tab === "property") {
      const c = COUNTRIES.find((x) => x.slug === f.place);
      placeLabel = c ? (isFr ? c.fr : c.en) : "";
      const surface = f.sizeMin && f.sizeMax
        ? `${f.sizeMin}–${f.sizeMax} m²`
        : f.sizeMin ? `${f.sizeMin} m² min` : f.sizeMax ? `${f.sizeMax} m² max` : "";
      summary = [
        isFr ? `Recherche Propriété — ${placeLabel || "l'Europe"}` : `Property Search — ${placeLabel || "Europe"}`,
        surface,
        f.bedrooms ? `${f.bedrooms} ${isFr ? "chambres" : "bedrooms"}` : "",
        f.budget ? `Budget ${f.budget}${/^\d+$/.test(String(f.budget)) ? " €" : ""}` : "",
      ].filter(Boolean).join(" · ");
    } else {
      const intent = f.intent || "charter";
      const intentLabel = intent === "purchase" ? (isFr ? "Achat" : "Purchase") : "Charter";
      const dates = intent === "charter" && f.from ? (f.to ? `${f.from} → ${f.to}` : f.from) : "";
      summary = [
        `Yacht — ${intentLabel}`,
        dates,
        intent === "charter" && f.passengers ? `${f.passengers} ${isFr ? "passagers" : "passengers"}` : "",
        f.length ? `${f.length} ${isFr ? "pieds" : "ft"}` : "",
        f.brand,
        f.cabins ? `${f.cabins} ${isFr ? "cabines" : "cabins"}` : "",
        f.budget ? `Budget ${f.budget}${/^\d+$/.test(String(f.budget)) ? " €" : ""}` : "",
      ].filter(Boolean).join(" · ");
    }
    sessionStorage.setItem("ftc_search", JSON.stringify({ type: tab, placeLabel, summary }));
    window.dispatchEvent(new CustomEvent("ftc:search", { detail: { type: tab, placeLabel, summary } }));
    navigate("/#contact");
    setTimeout(() => {
      const el = document.getElementById("contact");
      if (el) (window.__lenis ? window.__lenis.scrollTo(el, { offset: -20 }) : el.scrollIntoView());
    }, 150);
  };

  return (
    <section id="search" data-testid="search-section" className="bg-ivory py-24 md:py-32">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <Reveal className="flex flex-col items-center text-center mb-10">
          <span className="tricolore" style={{ width: "160px", height: "3px" }}>
            <span className="bg-frblue" /><span className="bg-white" /><span className="bg-frred" />
          </span>
          <span className="text-gold text-xs tracking-luxe uppercase mt-6">
            {isFr ? "Propriétés d'exception & yachts" : "Exceptional properties & yachts"}
          </span>
          <h2 className="font-serif-lux text-navy text-4xl sm:text-5xl lg:text-6xl font-light mt-5 max-w-3xl">
            {isFr ? "Votre Recherche Sur Mesure" : "Your Bespoke Search"}
          </h2>
          <p className="text-navy/60 font-light max-w-xl mt-5">
            {isFr
              ? "Dites-nous ce que vous cherchez — notre portefeuille privé s'ouvre sur demande."
              : "Tell us what you are looking for — our private portfolio opens on request."}
          </p>
        </Reveal>

        <Reveal delay={0.05} className="mb-14">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <span className="text-gold text-xs tracking-luxe uppercase">
                {tab === "property" ? (isFr ? "Propriétés d'Exception" : "Exceptional Properties") : (isFr ? "Yachts d'Exception" : "Exceptional Yachts")}
              </span>
              <h3 className="font-serif-lux text-navy text-3xl md:text-4xl font-light mt-4">
                {tab === "property"
                  ? (isFr ? "Nous trouvons la propriété de vos rêves" : "We find the property of your dreams")
                  : (isFr ? "Nous affrétons le yacht de vos rêves" : "We charter the yacht of your dreams")}
              </h3>
              <div className="mt-8 space-y-5">
                {(tab === "property"
                  ? (isFr
                    ? [
                        "Recherche sur mesure — villas, hôtels particuliers et domaines introuvables ailleurs.",
                        "Coordination des visites — nous organisons votre séjour et chaque visite, dans le moindre détail.",
                        "Accompagnement administratif — grâce à nos partenaires experts du domaine, chaque étape est sécurisée.",
                        "Aide à la négociation — nous défendons vos intérêts jusqu'à la signature.",
                      ]
                    : [
                        "Bespoke search — villas, private mansions and estates you will not find elsewhere.",
                        "Visit coordination — we organise your stay and every viewing, down to the last detail.",
                        "Administrative support — with our expert partners in the field, every step is secured.",
                        "Negotiation assistance — we defend your interests through to signature.",
                      ])
                  : (isFr
                    ? [
                        "Sélection sur mesure — voiliers, yachts à moteur et superyachts des plus grands chantiers.",
                        "Équipage & itinéraire — capitaine, chef et programme de navigation composés pour vous.",
                        "Coordination complète — transferts, ports d'attache et expériences à bord, sans friction.",
                        "Achat & vente — accompagnement par nos partenaires experts jusqu'à la signature.",
                      ]
                    : [
                        "Bespoke selection — sailing yachts, motor yachts and superyachts from the finest shipyards.",
                        "Crew & itinerary — captain, chef and cruising programme composed for you.",
                        "Full coordination — transfers, berths and on-board experiences, frictionless.",
                        "Purchase & sale — guidance from our expert partners through to signature.",
                      ])
                ).map((p, i) => (
                  <div key={i} className="flex gap-5 items-start" data-testid={`property-service-${i}`}>
                    <span className="font-serif-lux text-gold text-2xl leading-none mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-navy/70 font-light text-sm leading-relaxed">{p}</p>
                  </div>
                ))}
              </div>
              {tab === "yacht" && (
                <p data-testid="yacht-purchase-dept" className="mt-8 border border-gold/40 bg-gold/5 px-4 py-3 text-navy/80 text-sm font-light leading-relaxed">
                  <span className="text-gold tracking-luxe uppercase text-[10px] block mb-1">
                    {isFr ? "Département Achat de Yachts" : "Yacht Purchase Department"}
                  </span>
                  {isFr
                    ? "Recherche du navire idéal, expertise technique, négociation et accompagnement administratif jusqu'à la livraison — nos experts vous représentent."
                    : "Sourcing the right vessel, technical survey, negotiation and administrative support through to delivery — our experts represent you."}
                </p>
              )}
            </div>
            <div data-testid="search-showcase-photo" className="group relative overflow-hidden h-[300px] md:h-[420px]">
              <img
                key={tab}
                src={tab === "property" ? "/villa-privee.jpg" : "/yacht-signature.jpg"}
                alt={tab === "property"
                  ? (isFr ? "Villa privée au bord de l'eau" : "Private waterfront villa")
                  : (isFr ? "Superyacht avec hélicoptère" : "Superyacht with helicopter")}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 border border-transparent group-hover:border-gold/60 m-3 transition-all duration-500 pointer-events-none" />
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="border border-gold/30 bg-white/50 p-6 md:p-10">
            <div data-testid="search-tabs" className="flex justify-center gap-2 md:gap-3 mb-10">
              <button
                data-testid="search-tab-property"
                onClick={() => setTab("property")}
                className={`px-5 py-2 text-xs tracking-luxe uppercase border transition-colors ${tab === "property" ? "bg-navy text-gold border-navy" : "border-navy/20 text-navy/70 hover:border-gold hover:text-navy"}`}
              >
                {isFr ? "Propriétés" : "Properties"}
              </button>
              <button
                data-testid="search-tab-yacht"
                onClick={() => setTab("yacht")}
                className={`px-5 py-2 text-xs tracking-luxe uppercase border transition-colors ${tab === "yacht" ? "bg-navy text-gold border-navy" : "border-navy/20 text-navy/70 hover:border-gold hover:text-navy"}`}
              >
                {isFr ? "Yachts" : "Yachts"}
              </button>
            </div>

            <form onSubmit={submit} data-testid="search-form" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-7">
              {tab === "property" ? (
                <>
                  <div>
                    <label className={labelCls}>{isFr ? "Localisation" : "Location"}</label>
                    <select data-testid="search-place" value={f.place} onChange={upd("place")} className={inputCls}>
                      <option value="">{isFr ? "Toute l'Europe" : "All of Europe"}</option>
                      {COUNTRIES.map((c) => (
                        <option key={c.slug} value={c.slug}>{isFr ? c.fr : c.en}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>{isFr ? "Surface min (m²)" : "Min surface (m²)"}</label>
                    <input data-testid="search-size-min" type="number" min="0" value={f.sizeMin} onChange={upd("sizeMin")} className={inputCls} placeholder="200" />
                  </div>
                  <div>
                    <label className={labelCls}>{isFr ? "Surface max (m²)" : "Max surface (m²)"}</label>
                    <input data-testid="search-size-max" type="number" min="0" value={f.sizeMax} onChange={upd("sizeMax")} className={inputCls} placeholder="800" />
                  </div>
                  <div>
                    <label className={labelCls}>{isFr ? "Chambres" : "Bedrooms"}</label>
                    <select data-testid="search-bedrooms" value={f.bedrooms} onChange={upd("bedrooms")} className={inputCls}>
                      <option value="">{isFr ? "Indifférent" : "Any"}</option>
                      {["1–3", "4–6", "7–10", "10+"].map((b) => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>{isFr ? "Budget d'achat (€)" : "Purchase budget (€)"}</label>
                    <input data-testid="search-budget" type="number" min="0" value={f.budget} onChange={upd("budget")} className={inputCls} placeholder="10000000" />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className={labelCls}>{isFr ? "Type de demande" : "Request type"}</label>
                    <select data-testid="search-intent" value={f.intent || "charter"} onChange={upd("intent")} className={inputCls}>
                      <option value="charter">Charter</option>
                      <option value="purchase">{isFr ? "Achat" : "Purchase"}</option>
                    </select>
                  </div>
                  {(f.intent || "charter") === "charter" && (
                    <>
                      <div>
                        <label className={labelCls}>{isFr ? "Date d'arrivée" : "Arrival date"}</label>
                        <input data-testid="search-from" type="date" value={f.from || ""} onChange={upd("from")} className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>{isFr ? "Date de départ" : "Departure date"}</label>
                        <input data-testid="search-to" type="date" value={f.to || ""} onChange={upd("to")} className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>{isFr ? "Nombre de passagers" : "Passengers"}</label>
                        <input data-testid="search-passengers" type="number" min="1" value={f.passengers || ""} onChange={upd("passengers")} className={inputCls} placeholder="8" />
                      </div>
                    </>
                  )}
                  <div>
                    <label className={labelCls}>{isFr ? "Taille (pieds)" : "Length (ft)"}</label>
                    <select data-testid="search-length" value={f.length} onChange={upd("length")} className={inputCls}>
                      <option value="">{isFr ? "Toutes tailles" : "Any length"}</option>
                      {["60–80", "80–100", "100–130", "130+"].map((l) => (
                        <option key={l} value={l}>{l} {isFr ? "pieds" : "ft"}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>{isFr ? "Marque" : "Brand"}</label>
                    <input data-testid="search-brand" type="text" value={f.brand} onChange={upd("brand")} className={inputCls} placeholder="Benetti, Sunseeker…" />
                  </div>
                  <div>
                    <label className={labelCls}>{isFr ? "Nombre de cabines" : "Cabins"}</label>
                    <select data-testid="search-cabins" value={f.cabins} onChange={upd("cabins")} className={inputCls}>
                      <option value="">{isFr ? "Indifférent" : "Any"}</option>
                      {["2–4", "5–6", "7+"].map((c2) => (
                        <option key={c2} value={c2}>{c2}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>
                      {(f.intent || "charter") === "purchase"
                        ? (isFr ? "Budget d'achat (€)" : "Purchase budget (€)")
                        : "Budget"}
                    </label>
                    {(f.intent || "charter") === "purchase" ? (
                      <input data-testid="search-budget" type="number" min="0" value={f.budget} onChange={upd("budget")} className={inputCls} placeholder="10000000" />
                    ) : (
                      <select data-testid="search-budget" value={f.budget} onChange={upd("budget")} className={inputCls}>
                        <option value="">{isFr ? "Confidentiel" : "Discreet"}</option>
                        {SEARCH_BUDGETS.map((b) => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </>
              )}
              <div className="flex items-end">
                <button
                  type="submit"
                  data-testid="search-submit"
                  className="btn-lux w-full bg-navy text-gold hover:bg-navy-deep px-8 py-3 text-xs tracking-luxe uppercase transition-colors"
                >
                  {isFr ? "Lancer la Recherche" : "Start the Search"}
                </button>
              </div>
            </form>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- Marquee ---------------- */
export const Marquee = () => {
  const { t } = useLang();
  const items = [...t.marquee, ...t.marquee];
  return (
    <div data-testid="marquee" className="bg-navy py-6 border-y border-gold/20 overflow-hidden">
      <div className="marquee-track">
        {items.map((it, i) => (
          <span key={i} className="mx-8 font-serif-lux italic text-2xl md:text-3xl text-white/40 flex items-center gap-8">
            {it}<span className="text-gold not-italic">&#10022;</span>
          </span>
        ))}
      </div>
    </div>
  );
};

/* ---------------- About ---------------- */
export const About = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);
  const a = t.about;
  return (
    <section id="about" ref={ref} data-testid="about-section" className="bg-ivory py-28 md:py-40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-16 lg:gap-24 items-center">
        <div>
          <ChapterLabel chapter={a.chapter} />
          <Reveal><h2 className="font-serif-lux text-navy text-4xl sm:text-5xl lg:text-6xl font-light leading-tight">{a.title}</h2></Reveal>
          <Reveal delay={0.1}><p className="font-serif-lux italic text-2xl md:text-3xl text-gold mt-6 leading-snug">{a.lead}</p></Reveal>
          <Reveal delay={0.15}><p className="text-navy/70 mt-6 leading-relaxed font-light max-w-lg">{a.body}</p></Reveal>
          <div className="grid grid-cols-2 gap-x-8 gap-y-8 mt-12">
            {a.points.map((p, i) => (
              <Reveal key={i} delay={0.1 + i * 0.08}>
                <div className="gold-line w-10 mb-3" />
                <h3 className="font-serif-lux text-navy text-xl">{p.t}</h3>
                <p className="text-navy/60 text-sm mt-1 font-light">{p.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.1} className="relative h-[420px] md:h-[620px] overflow-hidden">
          <motion.div style={{ y }} className="absolute inset-0 -top-16 -bottom-16">
            <img src={IMAGES.provence} alt="Provence" className="w-full h-full object-cover" />
          </motion.div>
          <div className="absolute inset-0 border border-gold/40 m-4 pointer-events-none" />
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- Services ---------------- */
export const Services = () => {
  const { t } = useLang();
  const s = t.services;
  return (
    <section id="services" data-testid="services-section" className="bg-navy py-28 md:py-40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="max-w-2xl">
          <ChapterLabel chapter={s.chapter} />
          <Reveal><h2 className="font-serif-lux text-white text-4xl sm:text-5xl lg:text-6xl font-light">{s.title}</h2></Reveal>
          <Reveal delay={0.1}><p className="text-white/60 mt-5 font-light">{s.sub}</p></Reveal>
        </div>
        <div className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-5">
          {s.items.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % 4) * 0.06 }}
              className="group relative overflow-hidden h-[220px] md:h-[260px] cursor-pointer"
              data-testid={`service-${i}`}
            >
              <img
                src={SERVICE_IMAGES[i]}
                alt={item}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-navy/10 group-hover:from-navy/95 transition-colors duration-500" />
              <div className="absolute inset-0 border border-transparent group-hover:border-gold/50 m-3 transition-all duration-500 pointer-events-none" />
              <div className="absolute inset-0 p-5 md:p-6 flex flex-col justify-between">
                <span className="text-gold text-xs tracking-widest">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-serif-lux text-white text-lg md:text-xl leading-snug group-hover:text-gold-light transition-colors duration-500">{item}</h3>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Why Us ---------------- */
export const WhyUs = () => {
  const { t } = useLang();
  const w = t.why;
  return (
    <section id="why" data-testid="why-section" className="relative bg-navy-deep py-28 md:py-40 overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <img src={IMAGES.hotel} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <div className="flex justify-center"><ChapterLabel chapter={w.chapter} /></div>
          <Reveal><h2 className="font-serif-lux text-white text-4xl sm:text-5xl lg:text-6xl font-light">{w.title}</h2></Reveal>
        </div>
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gold/10">
          {w.items.map((item, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08} className="bg-navy-deep">
              <div className="p-10 h-full hover:bg-navy transition-colors duration-500">
                <span className="font-serif-lux text-gold text-4xl">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-serif-lux text-white text-2xl mt-4">{item.t}</h3>
                <p className="text-white/55 text-sm font-light mt-3 leading-relaxed">{item.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-20 max-w-3xl mx-auto text-center">
          <div className="gold-line w-16 mx-auto mb-8" />
          <p data-testid="founder-note" className="font-serif-lux italic text-white/90 text-xl md:text-2xl leading-relaxed">
            &ldquo;{w.note}&rdquo;
          </p>
          <p className="font-serif-lux text-gold-light text-3xl mt-8" style={{ fontStyle: "italic" }}>{w.signature}</p>
          <p className="text-gold/70 text-xs tracking-luxe uppercase mt-2">{w.signatureRole}</p>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- Testimonials ---------------- */
export const Testimonials = () => {
  const { t } = useLang();
  const tt = t.testimonials;
  return (
    <section id="testimonials" data-testid="testimonials-section" className="bg-ivory py-28 md:py-40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="flex justify-center"><ChapterLabel chapter={tt.chapter} /></div>
          <Reveal><h2 className="font-serif-lux text-navy text-4xl sm:text-5xl lg:text-6xl font-light">{tt.title}</h2></Reveal>
        </div>
        <div className="grid md:grid-cols-3 gap-10 lg:gap-16">
          {tt.items.map((item, i) => (
            <Reveal key={i} delay={i * 0.12}>
              <div data-testid={`testimonial-${i}`} className="text-center">
                <span className="font-serif-lux text-gold text-6xl leading-none">&ldquo;</span>
                <p className="font-serif-lux italic text-navy text-xl md:text-2xl leading-relaxed mt-2">{item.q}</p>
                <div className="gold-line w-12 mx-auto my-6" />
                <p className="text-navy text-sm tracking-widest uppercase font-medium">{item.a}</p>
                <p className="text-navy/50 text-xs mt-1 font-light">{item.r}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
